import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createNotificationApi } from "./notificationApi";
import "./notificationBell.css";

const FILTERS = [
    { id: "all", label: "All" },
    { id: "unread", label: "Unread" },
    { id: "read", label: "Read" },
];

const PREF_TOGGLES = [
    ["push_enabled", "Receive Push"],
    ["whatsapp_enabled", "Receive WhatsApp"],
    ["web_enabled", "Receive Web Notifications"],
    ["email_enabled", "Receive Email"],
    ["reminders_enabled", "Receive Reminder Notifications"],
    ["comments_enabled", "Receive Task Comments"],
    ["mentions_enabled", "Receive Mentions"],
    ["due_alerts_enabled", "Receive Due Date Alerts"],
    ["escalations_enabled", "Receive Escalation Alerts"],
    ["sound_enabled", "Notification sound"],
];

const NotificationCenter = ({ apiUrl, getToken, role = "staff" }) => {
    const navigate = useNavigate();
    const api = createNotificationApi({ apiUrl, getToken });
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [q, setQ] = useState("");
    const [prefs, setPrefs] = useState(null);

    const load = useCallback(async () => {
        const params = { q, limit: 80 };
        if (filter === "unread") params.unread = "1";
        if (filter === "read") params.read = "1";
        const result = await api.list(params);
        setItems(result.data || []);
    }, [apiUrl, getToken, filter, q]);

    useEffect(() => {
        load().catch(() => setItems([]));
        api.getPreferences().then(setPrefs).catch(() => null);
    }, [load]);

    const openItem = async (item) => {
        if (!item.read_at) await api.markRead([item.id]);
        if (item.task_id) await api.logOpened(item.task_id);
        navigate(item.deep_link || `/tasks?taskId=${item.task_id || ""}`);
    };

    return (
        <div className="notif-center">
            <h1>Notification Center</h1>
            <p>Read, filter, and control how task alerts reach you.</p>

            {prefs && (
                <div className="notif-center__prefs">
                    {PREF_TOGGLES.map(([key, label]) => (
                        <label key={key}>
                            <input
                                type="checkbox"
                                checked={Boolean(prefs[key])}
                                onChange={async (e) => {
                                    const next = { ...prefs, [key]: e.target.checked };
                                    setPrefs(next);
                                    await api.savePreferences({ [key]: e.target.checked });
                                }}
                            />
                            {label}
                        </label>
                    ))}
                    <label>
                        Digest
                        <select
                            value={prefs.digest_mode || "immediate"}
                            onChange={async (e) => {
                                const next = { ...prefs, digest_mode: e.target.value };
                                setPrefs(next);
                                await api.savePreferences({ digest_mode: e.target.value });
                            }}
                        >
                            <option value="immediate">Immediate</option>
                            <option value="daily">Daily digest (low priority)</option>
                        </select>
                    </label>
                </div>
            )}

            <div className="notif-center__toolbar">
                {FILTERS.map((f) => (
                    <button
                        key={f.id}
                        type="button"
                        onClick={() => setFilter(f.id)}
                        style={{ fontWeight: filter === f.id ? 700 : 400 }}
                    >
                        {f.label}
                    </button>
                ))}
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search notifications"
                />
                <button type="button" onClick={() => api.markRead(null, true).then(load)}>
                    Mark all read
                </button>
            </div>

            <div className="notif-center__list">
                {items.length === 0 && <p>No notifications.</p>}
                {items.map((item) => (
                    <div key={item.id} style={{ display: "flex", gap: 8 }}>
                        <button
                            type="button"
                            className={`notif-center__item${item.read_at ? "" : " is-unread"}`}
                            onClick={() => openItem(item)}
                        >
                            <div>
                                <strong>{item.title}</strong>
                                <div>{item.body}</div>
                                <small>{item.createdAt ? new Date(item.createdAt).toLocaleString() : ""}</small>
                            </div>
                        </button>
                        <button type="button" onClick={() => api.archive([item.id]).then(load)}>
                            Archive
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default NotificationCenter;
